import { useState, useEffect } from 'react';
import { Client } from '../page';
import WhatsAppChat from '@/components/feature/WhatsAppChat';

interface ClientModalProps {
  client: Client | null;
  onClose: () => void;
  onSave: (data: Partial<Client>) => Promise<void>;
  initialTab?: 'dados' | 'whatsapp';
}

const SITUATION_OPTIONS = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'prospecto', label: 'Prospecto' },
  { value: 'suspenso', label: 'Suspenso' },
  { value: 'encerrado', label: 'Encerrado' },
];

const SEGMENT_OPTIONS = [
  'Tecnologia', 'Saúde', 'Educação', 'Varejo', 'Indústria',
  'Serviços', 'Financeiro', 'Imobiliário', 'Agronegócio', 'Outro',
];

const STATES = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

export default function ClientModal({ client, onClose, onSave, initialTab = 'dados' }: ClientModalProps) {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'dados' | 'endereco' | 'obs' | 'whatsapp'>(
    initialTab as any || 'dados'
  );
  const [form, setForm] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    whatsapp: '',
    document: '',
    document_type: 'cnpj',
    segment: '',
    situation: 'ativo',
    website: '',
    notes: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    is_active: true,
  });

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || '',
        contact_name: client.contact_name || '',
        email: client.email || '',
        phone: client.phone || '',
        whatsapp: client.whatsapp || '',
        document: client.document || '',
        document_type: client.document_type || 'cnpj',
        segment: client.segment || '',
        situation: client.situation || 'ativo',
        website: client.website || '',
        notes: client.notes || '',
        address_street: client.address_street || '',
        address_number: client.address_number || '',
        address_complement: client.address_complement || '',
        address_neighborhood: client.address_neighborhood || '',
        address_city: client.address_city || '',
        address_state: client.address_state || '',
        address_zip: client.address_zip || '',
        is_active: client.is_active ?? true,
      });
    }
  }, [client]);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab as any);
    }
  }, [initialTab]);

  const set = (field: string, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const tabs = [
    { id: 'dados', label: 'Dados Cadastrais', icon: 'ri-user-line' },
    { id: 'endereco', label: 'Endereço', icon: 'ri-map-pin-line' },
    { id: 'obs', label: 'Observações', icon: 'ri-file-text-line' },
    ...(client ? [{ id: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line' }] : []),
  ] as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <h2 className="text-slate-800 font-semibold text-base">
            {client ? 'Editar Cliente' : 'Novo Cliente'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-6 shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {/* Tab: WhatsApp */}
            {activeTab === 'whatsapp' && client && (
              <WhatsAppChat 
                clientId={client.id}
                contactName={client.contact_name || client.name}
                phoneNumber={client.whatsapp || client.phone || ''}
              />
            )}
            {/* Tab: Dados Cadastrais */}
            {activeTab === 'dados' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Nome da empresa / pessoa <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    required
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                    placeholder="Razão social ou nome completo"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Nome do contato</label>
                    <input
                      type="text"
                      value={form.contact_name}
                      onChange={(e) => set('contact_name', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="Nome do responsável"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Situação</label>
                    <select
                      value={form.situation}
                      onChange={(e) => set('situation', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer"
                    >
                      {SITUATION_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">E-mail</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => set('email', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="email@empresa.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Telefone</label>
                    <input
                      type="text"
                      value={form.phone}
                      onChange={(e) => set('phone', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="(11) 9999-9999"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">WhatsApp</label>
                    <input
                      type="text"
                      value={form.whatsapp}
                      onChange={(e) => set('whatsapp', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Website</label>
                    <input
                      type="text"
                      value={form.website}
                      onChange={(e) => set('website', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="https://empresa.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Tipo de documento</label>
                    <select
                      value={form.document_type}
                      onChange={(e) => set('document_type', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer"
                    >
                      <option value="cnpj">CNPJ</option>
                      <option value="cpf">CPF</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      {form.document_type === 'cnpj' ? 'CNPJ' : 'CPF'}
                    </label>
                    <input
                      type="text"
                      value={form.document}
                      onChange={(e) => set('document', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder={form.document_type === 'cnpj' ? '00.000.000/0001-00' : '000.000.000-00'}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Segmento</label>
                  <select
                    value={form.segment}
                    onChange={(e) => set('segment', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer"
                  >
                    <option value="">Selecionar segmento</option>
                    {SEGMENT_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Tab: Endereço */}
            {activeTab === 'endereco' && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Logradouro</label>
                    <input
                      type="text"
                      value={form.address_street}
                      onChange={(e) => set('address_street', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="Rua, Avenida..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Número</label>
                    <input
                      type="text"
                      value={form.address_number}
                      onChange={(e) => set('address_number', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="123"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Complemento</label>
                    <input
                      type="text"
                      value={form.address_complement}
                      onChange={(e) => set('address_complement', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="Sala, Andar..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Bairro</label>
                    <input
                      type="text"
                      value={form.address_neighborhood}
                      onChange={(e) => set('address_neighborhood', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="Bairro"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-1">
                    <label className="block text-xs font-medium text-slate-600 mb-1">CEP</label>
                    <input
                      type="text"
                      value={form.address_zip}
                      onChange={(e) => set('address_zip', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="00000-000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Cidade</label>
                    <input
                      type="text"
                      value={form.address_city}
                      onChange={(e) => set('address_city', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700"
                      placeholder="Cidade"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Estado</label>
                    <select
                      value={form.address_state}
                      onChange={(e) => set('address_state', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 cursor-pointer"
                    >
                      <option value="">UF</option>
                      {STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Observações */}
            {activeTab === 'obs' && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Observações</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={8}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 text-slate-700 resize-none"
                  placeholder="Informações adicionais sobre o cliente..."
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{form.notes.length}/500</p>
              </div>
            )}
          </div>

          {/* Footer - Only show if not in WhatsApp tab */}
          {activeTab !== 'whatsapp' && (
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={saving || !form.name.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                {client ? 'Salvar alterações' : 'Cadastrar cliente'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
